import React, { useId, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { nominationService } from '@/shared/services/nominationService';
import {
    formatAnswerBy,
    formatAnswerDay,
    nomineeAnswerByRange,
} from '@/shared/utils/deadline';
import {
    usePreviewLock,
    PREVIEW_LOCK_NOTE_ID,
} from '@/shared/hooks/usePreviewLock';
import {
    OfferPlaceDialog,
    ReleaseToPoolDialog,
    TakeBackDialog,
} from './OfferChoiceDialogs';
import NomineeTermsSummary from './NomineeTermsSummary';
import ReleaseReasonDialog, { EndReasonSummary } from './ReleaseReasonDialog';
import { SAVE_FALLBACK, WriteStatus, refusalOf } from './reviewerStages';

const STATUS_TEXT = {
    offered: 'Offered',
    not_selected: 'Not offered',
    accepted: 'Accepted',
    declined: 'Declined',
    voided: 'Withdrawn by the program',
    unavailable: 'No longer available',
    released: 'Released',
    taken_back: 'Offer taken back',
    expired: 'Offer expired',
};

// Whether the nominee can be offered an open place, by the server's
// `offerable` marker: faculty read a take-back as `unavailable` and never
// receive the end reason that rules a released nominee out. An expired
// offer is offered again with its own button, since it holds its place.
const canBeOffered = (nomination) =>
    nomination.offerable === true && nomination.status !== 'expired';

// How an expired offer's place ended, by the server's `endedAs` marker.
const ENDED_AS_TEXT = {
    released_to_applicants: 'Released to applicants',
    offered_to_another: 'Place offered to another nominee',
};

const statusText = (nomination) =>
    ENDED_AS_TEXT[nomination.endedAs] ||
    STATUS_TEXT[nomination.status] ||
    nomination.status;

const DAYS_TO_ANSWER_MAX = 60;

const refusalOnly = (err) => ({
    message: err?.response?.data?.message || SAVE_FALLBACK,
    conflict: err?.response?.status === 409,
});

// The wording EmploymentAttestationSummary uses for the same requirements.
const UNMET_TEXT = {
    authorizedToWork: 'Authorized to work in the United States',
    minimumAge: 'At least the minimum age',
    creditHours: 'Registered for the minimum credit hours',
    semesterGpa: 'Semester GPA at or above the minimum',
};

// The reason on the last history entry for the status the nomination is in
// now: the nominee's own words on a decline, the coordinator's on a take-back.
const reasonFor = (nomination, status) =>
    nomination.status === status
        ? ([...(nomination.statusHistory || [])]
              .reverse()
              .find((entry) => entry.status === status)?.reason ?? null)
        : null;
const declineReasonOf = (nomination) => reasonFor(nomination, 'declined');
const takeBackReasonOf = (nomination) => reasonFor(nomination, 'taken_back');

const headingTag = (level) => `h${Math.min(Math.max(level, 2), 6)}`;

/**
 * The students a faculty member named on this position. Faculty mode reads
 * the name, the status and, for an accepted nominee, the program terms the
 * server sent them (the answers an applicant's faculty read): the address,
 * the 900 number and the employment flags are the program's, and a response
 * that carries them for faculty still renders none. Faculty read an accepted
 * student's address in the list of their LAs.
 */
export default function PositionNominees({
    nominations,
    mode,
    headingLevel = 3,
    access,
    position,
    releaseDate = null,
    onChange,
    rowRef,
}) {
    const headingId = useId();
    const locked = usePreviewLock();
    const [releasing, setReleasing] = useState(null);
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState(null);
    const [notice, setNotice] = useState('');
    // { kind: 'takeBack' | 'offer' | 'release' | 'other', nomination }
    const [dialog, setDialog] = useState(null);
    const [dialogError, setDialogError] = useState(null);
    if (!nominations || nominations.length === 0) return null;
    const Heading = headingTag(headingLevel);
    const coordinator = mode === 'coordinator';
    const canRelease = Boolean(access?.canOffer);
    const canOffer = Boolean(access?.canOffer);
    // An unknown count is treated as room: the server refuses an offer that
    // has no place to go and its message is shown.
    const noOpenPlace = position?.openSlots === 0;
    const { min: dayMin, max: dayMax } = nomineeAnswerByRange(
        releaseDate,
        DAYS_TO_ANSWER_MAX
    );

    const release = async (reason) => {
        const target = releasing;
        setReleasing(null);
        setBusy(true);
        setRefusal(null);
        setNotice('');
        try {
            await nominationService.release(target.id, reason, {
                status: target.status,
            });
            setNotice(`Released ${target.name}.`);
            onChange?.(`Released ${target.name}.`, target.id);
        } catch (err) {
            setRefusal(refusalOf(err, SAVE_FALLBACK));
        } finally {
            setBusy(false);
        }
    };

    const closeDialog = () => {
        setDialog(null);
        setDialogError(null);
    };

    // One write from a dialog. A success closes the dialog, says what
    // happened and reloads, naming the row whose status changed so focus
    // goes there rather than to the page; a refusal stays in the dialog.
    const act = async (write, message, changedId) => {
        setBusy(true);
        setDialogError(null);
        setRefusal(null);
        setNotice('');
        try {
            await write();
        } catch (err) {
            setDialogError(refusalOnly(err));
            setBusy(false);
            return;
        }
        setBusy(false);
        setDialog(null);
        setNotice(message);
        onChange?.(message, changedId);
    };

    const target = dialog?.nomination;
    const takeBack = (reason) =>
        act(
            () => nominationService.takeBack(target.id, reason),
            `Took back the offer to ${target.name}.`,
            target.id
        );
    const offerAgain = ({ respondByDay }) =>
        act(
            () => nominationService.offer(target.id, { respondByDay }),
            `Offered ${target.name} the place${
                dialog.kind === 'offer' ? '' : ' again'
            }.`,
            target.id
        );
    const releaseToPool = () =>
        act(
            () => nominationService.releaseExpired(target.id),
            'The place is open to students who applied.',
            target.id
        );
    const offerOther = ({ respondByDay, choice }) => {
        const chosen = nominations.find((n) => n.id === choice);
        return act(
            () =>
                nominationService.offer(choice, {
                    respondByDay,
                    releaseFrom: target.id,
                }),
            `Offered ${chosen?.name || 'the nominee'} the place.`,
            choice
        );
    };

    const others = (expired) =>
        nominations.filter((n) => n.id !== expired.id && canBeOffered(n));
    // An expired offer holds a place that Offer again and Offer to another
    // nominee can use, so with one on the list a place is not missing.
    const holdsExpired = nominations.some((n) => n.status === 'expired');

    return (
        <section aria-labelledby={headingId} className="mt-3">
            <Heading id={headingId} className="h6">
                Named by the faculty member
            </Heading>
            <ul className="list-unstyled mb-0">
                {nominations.map((nomination) => {
                    const unmet = coordinator
                        ? nomination.employmentUnmet || []
                        : [];
                    return (
                        <li
                            key={nomination.id}
                            className="mb-2"
                            tabIndex={-1}
                            ref={(node) => rowRef?.(nomination.id, node)}
                        >
                            <span className="fw-semibold">
                                {nomination.name}
                            </span>
                            {`, ${statusText(nomination)}`}
                            {coordinator && (
                                <span className="d-block small text-body-secondary">
                                    {[nomination.email, nomination.studentId]
                                        .filter(Boolean)
                                        .join(', ')}
                                </span>
                            )}
                            {coordinator && declineReasonOf(nomination) && (
                                <span className="d-block mt-1">
                                    <span className="d-block small text-body-secondary">
                                        The nominee's reason for declining
                                    </span>
                                    <span className="d-block text-break">
                                        {declineReasonOf(nomination)}
                                    </span>
                                </span>
                            )}
                            {coordinator && takeBackReasonOf(nomination) && (
                                <span className="d-block mt-1">
                                    <span className="d-block small text-body-secondary">
                                        Why the offer was taken back
                                    </span>
                                    <span className="d-block text-break">
                                        {takeBackReasonOf(nomination)}
                                    </span>
                                </span>
                            )}
                            {coordinator && nomination.endReason && (
                                <span className="d-block mt-1">
                                    <span className="d-block small text-body-secondary">
                                        Why it ended
                                    </span>
                                    <EndReasonSummary
                                        endReason={nomination.endReason}
                                    />
                                </span>
                            )}
                            {nomination.status === 'accepted' && (
                                <NomineeTermsSummary
                                    terms={nomination.terms}
                                    reviewer={coordinator}
                                />
                            )}
                            {canRelease && nomination.status === 'accepted' && (
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="d-block mt-1"
                                    disabled={busy || locked}
                                    aria-describedby={
                                        locked
                                            ? PREVIEW_LOCK_NOTE_ID
                                            : undefined
                                    }
                                    onClick={() => setReleasing(nomination)}
                                >
                                    {`Release ${nomination.name} from this position`}
                                </Button>
                            )}
                            {nomination.status === 'offered' &&
                                nomination.respondBy && (
                                    <span className="d-block small">
                                        {`Answer by ${formatAnswerBy(nomination.respondBy)}`}
                                    </span>
                                )}
                            {nomination.status === 'expired' && (
                                <span className="d-block">
                                    {`The offer to ${nomination.name} expired${
                                        nomination.respondBy
                                            ? ` on ${formatAnswerDay(nomination.respondBy)}`
                                            : ''
                                    } without an answer.`}
                                </span>
                            )}
                            {canOffer && nomination.status === 'offered' && (
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="d-block mt-1"
                                    disabled={busy || locked}
                                    aria-describedby={
                                        locked
                                            ? PREVIEW_LOCK_NOTE_ID
                                            : undefined
                                    }
                                    onClick={() =>
                                        setDialog({
                                            kind: 'takeBack',
                                            nomination,
                                        })
                                    }
                                >
                                    {`Take back the offer to ${nomination.name}`}
                                </Button>
                            )}
                            {canOffer && nomination.status === 'expired' && (
                                <div className="d-grid gap-2 d-sm-flex flex-sm-wrap mt-1">
                                    {[
                                        ['expiredOffer', 'Offer again'],
                                        ['release', 'Release to applicants'],
                                        ...(others(nomination).length > 0
                                            ? [
                                                  [
                                                      'other',
                                                      'Offer to another nominee',
                                                  ],
                                              ]
                                            : []),
                                        ['takeBack', 'Take back the offer'],
                                    ].map(([kind, label]) => (
                                        <Button
                                            key={kind}
                                            variant={
                                                kind === 'takeBack'
                                                    ? 'outline-danger'
                                                    : 'outline-primary'
                                            }
                                            size="sm"
                                            disabled={busy || locked}
                                            aria-describedby={
                                                locked
                                                    ? PREVIEW_LOCK_NOTE_ID
                                                    : undefined
                                            }
                                            onClick={() =>
                                                setDialog({
                                                    kind,
                                                    nomination,
                                                })
                                            }
                                        >
                                            {label}
                                            <span className="visually-hidden">
                                                {` ${nomination.name}`}
                                            </span>
                                        </Button>
                                    ))}
                                </div>
                            )}
                            {nomination.status === 'expired' &&
                                !coordinator &&
                                canOffer &&
                                others(nomination).length === 0 && (
                                    <span className="d-block small text-body-secondary mt-1">
                                        To name someone new, ask the
                                        coordinator.
                                    </span>
                                )}
                            {canOffer &&
                                canBeOffered(nomination) &&
                                !noOpenPlace && (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="d-block mt-1"
                                        disabled={busy || locked}
                                        aria-describedby={
                                            locked
                                                ? PREVIEW_LOCK_NOTE_ID
                                                : undefined
                                        }
                                        onClick={() =>
                                            setDialog({
                                                kind: 'offer',
                                                nomination,
                                            })
                                        }
                                    >
                                        {`Offer the place to ${nomination.name}`}
                                    </Button>
                                )}
                            {unmet.length > 0 && (
                                <span className="d-block">
                                    <Badge bg="warning" text="dark">
                                        Unmet employment requirements
                                    </Badge>
                                    <span className="ms-2">
                                        {unmet
                                            .map(
                                                (key) => UNMET_TEXT[key] || key
                                            )
                                            .join('; ')}
                                    </span>
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
            {canOffer &&
                noOpenPlace &&
                !holdsExpired &&
                nominations.some(canBeOffered) && (
                    <p className="small text-body-secondary mt-2 mb-0">
                        No place is open right now. Take back or release an
                        offer to open one.
                    </p>
                )}
            <WriteStatus
                refusal={refusal}
                notice={notice}
                onReload={onChange}
            />
            <TakeBackDialog
                show={dialog?.kind === 'takeBack'}
                name={target?.name || 'this student'}
                busy={busy}
                error={dialogError}
                onConfirm={takeBack}
                onHide={closeDialog}
                onReload={() => {
                    closeDialog();
                    onChange?.();
                }}
            />
            <OfferPlaceDialog
                show={
                    dialog?.kind === 'offer' || dialog?.kind === 'expiredOffer'
                }
                title={
                    dialog?.kind === 'offer'
                        ? `Offer the place to ${target?.name}?`
                        : `Offer the place to ${target?.name} again?`
                }
                intro={
                    dialog?.kind === 'offer'
                        ? 'They are emailed the offer with the date to answer by.'
                        : 'They get a new offer with a new date to answer by, and are emailed it.'
                }
                confirmLabel="Make the offer"
                min={dayMin}
                max={dayMax}
                busy={busy}
                error={dialogError}
                onConfirm={offerAgain}
                onHide={closeDialog}
                onReload={() => {
                    closeDialog();
                    onChange?.();
                }}
            />
            <OfferPlaceDialog
                show={dialog?.kind === 'other'}
                title="Offer the place to another nominee"
                intro={`${target?.name || 'The first nominee'} keeps no hold on the place. The nominee you pick is emailed the offer.`}
                confirmLabel="Make the offer"
                choices={
                    dialog?.kind === 'other'
                        ? others(target).map((n) => ({
                              id: n.id,
                              label: n.name,
                          }))
                        : []
                }
                min={dayMin}
                max={dayMax}
                busy={busy}
                error={dialogError}
                onConfirm={offerOther}
                onHide={closeDialog}
                onReload={() => {
                    closeDialog();
                    onChange?.();
                }}
            />
            <ReleaseToPoolDialog
                show={dialog?.kind === 'release'}
                name={target?.name || 'this student'}
                busy={busy}
                error={dialogError}
                onConfirm={releaseToPool}
                onHide={closeDialog}
                onReload={() => {
                    closeDialog();
                    onChange?.();
                }}
            />
            <ReleaseReasonDialog
                show={Boolean(releasing)}
                side="reviewer"
                title={`Release ${releasing?.name || 'this student'} from this position?`}
                intro="Their assignment ends, the place opens again, and the student, the section's faculty and the program are emailed."
                confirmLabel="Release from this position"
                onConfirm={release}
                onHide={() => setReleasing(null)}
            />
        </section>
    );
}
